import React, { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Input } from '@actual-app/components/input';
import { styles } from '@actual-app/components/styles';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import { send } from '@actual-app/core/platform/client/connection';
import * as monthUtils from '@actual-app/core/shared/months';
import { addDays } from '@actual-app/core/shared/mtxbudget';
import { shortfallByPay } from '@actual-app/core/shared/mtxbudget-parcours';
import type {
  WalkInputs,
  WalkStep,
} from '@actual-app/core/shared/mtxbudget-parcours';

import { useNavigate } from '#hooks/useNavigate';

import { useByContent } from './byContent';
import { NotConfigured } from './QuinzainePage';
import { solveTask } from './solver';
import {
  hoursLabel,
  Line,
  longDate,
  MtxPage,
  Section,
  textDate,
  useMoney,
} from './ui';
import { useMtxbudget } from './useMtxbudget';
import { useParcours } from './useParcours';

function parseAmount(value: string): number | null {
  const amount = Number(value.replace(/\s/g, '').replace(',', '.'));
  return Number.isFinite(amount) && amount > 0
    ? Math.round(amount * 100)
    : null;
}

/**
 * The walk as it stands once the amount received is known: the same walk,
 * the pay replaced by what came in. Null while it is being computed.
 */
function useWalkWithReceived(
  inputs: WalkInputs | null,
  payday: string | undefined,
  received: number | null,
): { steps: WalkStep[] | null; inputs: WalkInputs | null } {
  const asked = useByContent(
    inputs && payday && received !== null
      ? {
          ...inputs,
          pays: {
            ...inputs.pays,
            [payday]: { ...inputs.pays?.[payday], received },
          },
        }
      : null,
  );
  const origin = useRef(`jour-de-paie-${Math.random()}`).current;
  const [steps, setSteps] = useState<WalkStep[] | null>(null);
  useEffect(() => {
    if (!asked) {
      setSteps(null);
      return;
    }
    let cancelled = false;
    void solveTask({ kind: 'walk', inputs: asked }, origin).then(result => {
      if (!cancelled) {
        setSteps(result);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [asked, origin]);
  return { steps, inputs: asked };
}

export function JourDePaiePage() {
  const { t } = useTranslation();
  const money = useMoney();
  const navigate = useNavigate();
  const { config, saveConfig, today, isLoading } = useMtxbudget();
  const parcours = useParcours();
  const [entered, setEntered] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const planned = parcours.steps[0];
  const received = entered === null ? null : parseAmount(entered);
  const preview = useWalkWithReceived(
    parcours.inputs,
    planned?.payday,
    received,
  );
  if (!config || !planned) {
    return (
      <MtxPage title={t('Jour de paie')} back="/quinzaine">
        {!isLoading && <NotConfigured />}
        {config && parcours.status === 'loading' && (
          <Text style={{ padding: 16, color: theme.pageTextSubdued }}>
            <Trans>Calcul en cours…</Trans>
          </Text>
        )}
      </MtxPage>
    );
  }

  // The pay as it stands once its amount is known, else as planned.
  const shown =
    received !== null && preview.steps && preview.inputs
      ? { steps: preview.steps, inputs: preview.inputs }
      : { steps: parcours.steps, inputs: parcours.inputs };
  const steps = shown.steps;
  const step = steps[0];
  const cushion = config.cushion;
  const debts = step.debts.reduce((sum, d) => sum + d.extra, 0);
  const projects = step.needs.reduce((sum, n) => sum + n.paid, 0);
  // Kept before this pay for the same later pay, and what this one adds.
  // What the account really holds before the pay: a gap the pay before
  // left is still there, and comes first.
  const before = step.cashIn - step.refund;
  const keptBefore = Math.max(0, before - cushion);
  const keepNow = Math.max(0, step.setAside - keptBefore);
  const later = steps.find(s => s.payday === step.setAsideFor);
  const margin = Math.max(0, step.cashOut - cushion - step.setAside);
  const cashCount = step.charges.filter(
    c => !c.skipped && c.cardId === undefined,
  ).length;
  const hours = (step.hours[0] + step.hours[1]) / 2;
  // What the charges miss, the cushion aside: the same figure as everywhere.
  const missing = shown.inputs
    ? (shortfallByPay(shown.inputs)[step.payday] ?? 0)
    : 0;
  // What the account really ends at, before anything comes to the rescue.
  const ends = step.cashOut - step.shortfall;

  async function validate() {
    if (received === null || !config) {
      return;
    }
    saveConfig({
      ...config,
      pays: {
        ...config.pays,
        [planned.payday]: { ...config.pays[planned.payday], received },
      },
    });
    // Actual fills the categories from their templates, in priority order;
    // what is left stays "to budget" as the margin.
    await send('budget/overwrite-goal-template', {
      month: monthUtils.monthFromDate(today),
    });
    setSaved(true);
  }

  return (
    <MtxPage title={t('Jour de paie')} back="/quinzaine">
      <View style={{ gap: 12, paddingBottom: 20 }}>
        <Section style={{ padding: 16, gap: 8 }}>
          <Text
            style={{
              fontSize: 13,
              fontWeight: 500,
              color: theme.pageTextSubdued,
            }}
          >
            {t('Montant reçu le {{date}}', { date: longDate(step.payday) })}
          </Text>
          <Input
            aria-label={t('Montant reçu')}
            inputMode="decimal"
            placeholder={(planned.net / 100).toFixed(2)}
            value={entered ?? ''}
            onChangeValue={setEntered}
            style={{
              minHeight: 52,
              fontSize: 26,
              fontWeight: 600,
              minWidth: 0,
              ...styles.tnum,
            }}
          />
          <Text style={{ fontSize: 13, color: theme.pageTextSubdued }}>
            {t('Attendu : environ {{amount}}, pour {{hours}} par semaine.', {
              amount: money(planned.net),
              hours: hoursLabel(hours),
            })}
          </Text>
        </Section>

        {missing > 0 && (
          <Section
            style={{
              padding: '12px 16px',
              gap: 8,
              backgroundColor: theme.errorBackground,
              border: `1px solid ${theme.errorBorder}`,
            }}
          >
            <Text style={{ fontWeight: 600, color: theme.errorText }}>
              {t('Cette paie ne suffit pas : il manque {{amount}}', {
                amount: money(missing),
              })}
            </Text>
            <Button
              variant="primary"
              onPress={() => navigate('/quinzaine/optimiser')}
              style={{ minHeight: 44, fontWeight: 600 }}
            >
              <Trans>Voir les solutions</Trans>
            </Button>
          </Section>
        )}

        {keepNow > 0 && later && (
          <Section
            style={{
              padding: '12px 16px',
              gap: 4,
              backgroundColor: theme.warningBackground,
              border: `1px solid ${theme.warningBorder}`,
            }}
          >
            <Text
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: theme.warningText,
              }}
            >
              <Trans>À garder dans le compte</Trans>
            </Text>
            <View
              style={{
                flexDirection: 'row',
                flexWrap: 'wrap',
                alignItems: 'baseline',
                columnGap: 8,
              }}
            >
              <Text
                style={{
                  fontSize: 28,
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                  color: theme.pageTextDark,
                  ...styles.tnum,
                }}
              >
                {money(keepNow)}
              </Text>
              <Text style={{ fontSize: 13 }}>
                {t('pour la paie du {{date}}', {
                  date: textDate(later.payday),
                })}
              </Text>
            </View>
            <Text style={{ fontSize: 13 }}>
              {keptBefore > 0
                ? t(
                    'Seule, elle ne suffira pas : {{charges}} de charges pour {{net}} de salaire. Avec ce qui est déjà gardé ({{before}}), elle aura {{total}} de plus.',
                    {
                      charges: money(later.totalCharges),
                      net: money(later.net),
                      before: money(keptBefore),
                      total: money(step.setAside),
                    },
                  )
                : t(
                    'Seule, elle ne suffira pas : {{charges}} de charges pour {{net}} de salaire.',
                    {
                      charges: money(later.totalCharges),
                      net: money(later.net),
                    },
                  )}
            </Text>
          </Section>
        )}

        <Section title={t('Ce que cette paie doit faire')}>
          <Line
            title={t('Dans le compte avant la paie')}
            detail={
              keptBefore > 0
                ? t('coussin et {{amount}} déjà gardés', {
                    amount: money(keptBefore),
                  })
                : undefined
            }
            value={money(before)}
            valueColor={before < 0 ? theme.errorText : undefined}
          />
          <Line
            title={t("Payer les échéances jusqu'au {{date}}", {
              date: textDate(addDays(step.nextPayday, -1)),
            })}
            detail={t('{{count}} échéances', { count: cashCount })}
            value={`− ${money(step.totalCharges)}`}
          />
          {debts > 0 && (
            <Line
              title={t('Rembourser des dettes, en plus des minimums')}
              value={`− ${money(debts)}`}
            />
          )}
          {projects > 0 && (
            <Line
              title={t('Mettre de côté pour vos projets')}
              value={`− ${money(projects)}`}
            />
          )}
          {step.shortfall > 0 ? (
            <Line
              last
              title={t('Reste dans le compte')}
              detail={t('sans solution, le compte passe sous le coussin')}
              value={money(ends)}
              valueColor={ends < 0 ? theme.errorText : theme.warningText}
            />
          ) : (
            <>
              <Line
                title={
                  later
                    ? t('Garder pour la paie du {{date}}', {
                        date: textDate(later.payday),
                      })
                    : t('Garder pour une paie suivante')
                }
                detail={
                  later
                    ? t('à ne pas dépenser avant le {{date}}', {
                        date: textDate(later.payday),
                      })
                    : t("aucune n'en a besoin")
                }
                value={keepNow > 0 ? `− ${money(keepNow)}` : money(0)}
                valueColor={
                  keepNow > 0 ? theme.warningText : theme.pageTextSubdued
                }
              />
              <Line
                title={t('Coussin laissé dans le compte')}
                value={money(Math.min(cushion, step.cashOut))}
              />
              <Line
                last
                title={t('Marge libre')}
                detail={t('vous pouvez la dépenser')}
                value={money(margin)}
                valueColor={
                  margin > 0 ? theme.noticeTextLight : theme.pageTextSubdued
                }
              />
            </>
          )}
        </Section>

        <Button
          variant="primary"
          isDisabled={received === null || saved}
          onPress={() => void validate()}
          style={{ minHeight: 48, fontWeight: 600 }}
        >
          {saved ? t('Paie validée') : t('Valider la paie')}
        </Button>
        <Button
          variant="bare"
          onPress={() =>
            navigate(`/quinzaine/parcours/paie?paie=${step.payday}`)
          }
          style={{ minHeight: 44, fontWeight: 600 }}
        >
          <Trans>Voir le détail de cette paie</Trans>
        </Button>
      </View>
    </MtxPage>
  );
}
